<template>
    <div class="space-y-1" :data-refresh-tick="refreshTick">
        <!-- Parts with real four-side padding get the same editor as Gauge.
             Slider and Switch Knob opt into raw LVGL sides while retaining
             the legacy padding/2 fallback for designs that predate them. -->
        <PaddingEditor v-if="hasPaddingEditor"
            :node="node"
            :part-name="partName"
            :state="state"
            :overall-label="isDirectKnobPaddingPart ? 'padding' : 'overall'"
            :overall-scale="isDirectKnobPaddingPart ? 0.5 : 1"
            :all-as-sides="isDirectKnobPaddingPart"
            :collapsible="isDirectKnobPaddingPart" />
        <template v-for="(meta, propName) in sortedProperties" :key="propName">
            <!-- Internal properties (keep_circle, bg_opa, text_font_family, …) are
                 never rendered as standalone UI controls. -->
            <template v-if="!meta.internal">
                <!-- One grid row per property: label | control | status. The
                     control column shrinks (minmax(0,1fr)) and the status column
                     is a fixed width, so nothing overlaps at any sidebar width;
                     `wide` moves space-hungry controls to their own line when the
                     row gets narrow. -->
                <!-- Explicit vs inherited is a pure UI visualization of
                     node.hasExplicitStyle(); no separate state is tracked here.
                     In a non-default state the fallback is this part's default
                     state ("inherited"), in the default state it is the
                     theme/schema default. -->
                <property-row
                    :label="rowLabel(propName, meta)"
                    :wide="isWide(propName, meta)"
                    :resettable="true"
                    :modified="isExplicit(propName)"
                    :inherited="state !== 'default'"
                    @reset="resetValue(propName)">
                    <!-- Radius with optional keep-circle lock toggle -->
                    <form-input v-if="propName === 'radius'"
                        :key="`radius-${partName}-${state}-${radiusTick}`"
                        :default_val="hasKeepCircle"
                        :disabled="node.style(partName, 'default').keep_circle"
                        checkbox_tooltip="rounded shape"
                        type="number" :no_scroll="true"
                        :min="meta.min ?? 0" :max="meta.max"
                        :model-value="styleValue(propName)"
                        @update:disabled="toggleKeepCircle(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />

                    <!-- border_side is a bitmask, not a number the user should
                         ever type: a dedicated toggle-row editor replaces the
                         generic integer input. It stores and reads the same raw
                         LVGL bitmask, so nothing below the UI layer changes. -->
                    <border-side-editor v-else-if="propName === 'border_side'"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />

                    <!-- Color picker -->
                    <color-picker v-else-if="meta.type === 'color'"
                        :pureColor="styleValue(propName)"
                        @update:pureColor="onColorUpdate(propName, $event)"
                        useType="pure" :format="meta.format ?? 'hex'" lang="En" shape="circle"
                        v-on:pureColorChange="setStyleValue(propName, $event)" />

                    <!-- Checkbox (boolean) — label comes from the row, not the control -->
                    <form-checkbox v-else-if="meta.type === 'bool'"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />

                    <!-- Enum select — values may be plain scalars or { value, label } objects -->
                    <form-select v-else-if="meta.type === 'enum'"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)">
                        <option v-for="v in meta.values" :key="optVal(v)" :value="optVal(v)">{{ optLabel(v) }}</option>
                    </form-select>

                    <!-- Text input (string) -->
                    <input v-else-if="meta.type === 'string'" type="text"
                        class="w-full min-w-0 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                        :value="styleValue(propName)"
                        @input="setStyleValue(propName, $event.target.value)" />

                    <!-- Project Resource reference (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
                         Mirrors PropertiesPanel.vue's resource-property loops: a
                         resourceType-registered override component (e.g.
                         FontFamilyPicker for 'font' — merges internal theme/icon
                         fonts alongside project fonts, and stores the wire-format
                         string directly, not a uuid) wins when one is registered
                         in resourceUIRegistry.js, else the bare ResourcePicker.
                         Without this, a part-scoped resource property (e.g.
                         chart.js's x_axis/y_axis `font`) silently fell back to the
                         generic ResourcePicker: project-fonts-only listing, and a
                         raw resource uuid stored/exported instead of the resolved
                         file path — this makes every resourceType behave
                         identically regardless of whether it lives at the
                         top level or inside a part. -->
                    <component
                        v-else-if="meta.type === 'resource' && resourceOverride(meta.resourceType)"
                        :is="resourceOverride(meta.resourceType)"
                        :resource-type="meta.resourceType"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />
                    <ResourcePicker v-else-if="meta.type === 'resource'"
                        :resource-type="meta.resourceType"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />

                    <!-- Number input (int/float) — default for unknown type too -->
                    <form-input v-else
                        type="number"
                        :min="meta.min ?? 0"
                        :max="meta.max"
                        :step="meta.type === 'float' ? (meta.step ?? 0.1) : meta.step"
                        :model-value="styleValue(propName)"
                        @update:model-value="setStyleValue(propName, $event)" />

                </property-row>
            </template>
        </template>
    </div>
</template>

<script>
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import ResourcePicker from '../resources/ResourcePicker.vue';
import BorderSideEditor from './BorderSideEditor.vue';
import PaddingEditor from './PaddingEditor.vue';
import PropertyRow from './PropertyRow.vue';
import { getPropertyEditorComponent } from '../resources/resourceUIRegistry.js';

// Property editors that need more horizontal room than a narrow sidebar can
// give them: their row renders the control on its own line below the label
// until the row is wide enough again (see PropertyRow.vue).
const WIDE_PROPERTIES = ['border_side'];

export default {
    name: 'PartPropertiesPanel',
    components: { ColorPicker, ResourcePicker, BorderSideEditor, PaddingEditor, PropertyRow },

    props: {
        node:     { type: Object, required: true },
        partName: { type: String, required: true },
        only:     { type: Array,  default: null },
        exclude:  { type: Array,  default: null },
        // Semantic state name (see node.getSupportedStates()/stateFormat.js).
        // 'default' (the normal case) routes through styleProp()/setStyle();
        // any other value routes through stateStyleProp()/setStateStyle() —
        // this component has no other state-specific logic at all, it just
        // picks which pair of node methods to call.
        state:    { type: String, default: 'default' },
    },

    data() {
        return { radiusTick: 0, refreshTick: 0 };
    },

    // node is a plain (non-Vue-reactive) Konva instance by design — see
    // ConfigManager.js's markRaw() on styleResolver — so nothing here
    // re-renders automatically when a style value changes underneath it.
    // Subscribe to this part's own style-refresh events (fired for both
    // default and state-scoped writes, see ObjectGroup.refreshStyle) and bump
    // a local reactive counter to force a re-render; refreshTick itself is
    // never read for its value, only to register as a render dependency.
    mounted() {
        this._styleRefreshSubs = Object.keys(this.partDef.properties ?? {}).map(propName => {
            const handler = () => { this.refreshTick++; };
            this.node.onStyle(this.partName, propName, handler);
            return { propName, handler };
        });
    },

    beforeUnmount() {
        for (const { propName, handler } of this._styleRefreshSubs ?? []) {
            this.node.offStyle(this.partName, propName, handler);
        }
    },

    computed: {
        partDef() {
            return this.node.getStyleDefinition(this.partName) ?? { properties: {} };
        },

        hasKeepCircle() {
            return this.node.hasStyleProperty(this.partName, 'keep_circle');
        },

        isDirectKnobPaddingPart() {
            return this.partName === 'knob' && this.partDef.paddingSidesAuthoritative === true;
        },

        hasPaddingEditor() {
            const properties = this.partDef.properties ?? {};
            const fields = ['padding', 'pad_top', 'pad_right', 'pad_bottom', 'pad_left'];
            if (!fields.every(prop => prop in properties)) return false;
            if (this.only && !fields.every(prop => this.only.includes(prop))) return false;
            if (this.exclude && fields.some(prop => this.exclude.includes(prop))) return false;
            return true;
        },

        // Non-color properties first, colors at the bottom — within each group
        // the schema definition order is preserved.
        sortedProperties() {
            let props = Object.entries(this.partDef.properties ?? {});
            if (this.only)    props = props.filter(([k]) => this.only.includes(k));
            if (this.exclude) props = props.filter(([k]) => !this.exclude.includes(k));
            if (this.hasPaddingEditor) {
                props = props.filter(([k]) => !['padding', 'pad_top', 'pad_right', 'pad_bottom', 'pad_left'].includes(k));
            }
            const nonColor = props.filter(([, m]) => m?.type !== 'color');
            const color    = props.filter(([, m]) => m?.type === 'color');
            return Object.fromEntries([...nonColor, ...color]);
        },
    },

    methods: {
        // Resolved value for one property — the Property Editor's only reason
        // to query a single property instead of a whole part (see
        // node.style() / v2/StyleResolver.js). Routes through the state-aware
        // API only when a non-default state is selected — no other branching
        // on state anywhere else in this component. Always passes 'default'
        // explicitly (never omits it) so this panel's own default-state
        // fields stay true to the stored default even while a state preview
        // is active elsewhere — see ObjectGroup.style()'s state-omission/
        // preview semantics.
        styleValue(propName) {
            return this.state === 'default'
                ? this.node.styleProp(this.partName, propName, 'default')
                : this.node.stateStyleProp(this.partName, this.state, propName);
        },

        // The sole mutation entry point for style properties.
        setStyleValue(propName, value) {
            if (this.state === 'default') this.node.setStyle(this.partName, propName, value);
            else this.node.setStateStyle(this.partName, this.state, propName, value);
        },

        // Explicit-vs-inherited is a pure UI concept: the storage model
        // already preserves the distinction (see StyleResolver.js) — this
        // just asks node.hasExplicitStyle(), never tracking anything of its
        // own.
        isExplicit(propName) {
            return this.node.hasExplicitStyle(this.partName, propName, this.state);
        },

        // Revert an explicit state override back to inherited — deletes the
        // stored value via node.resetStateStyle(), nothing else.
        resetValue(propName) {
            this.node.resetStateStyle(this.partName, this.state, propName);
        },

        // vue3-colorpicker emits update:pureColor and pureColorChange together,
        // from the same debounced call, carrying the identical final value
        // (see node_modules/vue3-colorpicker's X()/j() — both events fire from
        // one debounced function). There is no separate cheap "live drag"
        // event here, so this handler must NOT write the value — only capture
        // the pre-change value for history, using the OLD value before
        // pureColorChange's setStyleValue (below) performs the one real
        // write+notify. Writing here too would make setStyle's change-
        // detection see no difference and silently skip refreshStyle.
        onColorUpdate(propName, _newValue) {
            const cacheKey = this.state === 'default'
                ? `${this.partName}:${propName}`
                : `${this.partName}:${this.state}:${propName}`;
            const layer = this.node.getLayer?.();
            if (layer && !layer._historySuppress) {
                layer._beforeColors ??= {};
                layer._beforeColors[cacheKey] ??= this.styleValue(propName);
            }
        },

        // Widget-specific component to use for a resourceType's editor instead
        // of the bare ResourcePicker, if one is registered — same lookup
        // PropertiesPanel.vue uses for its own (top-level) resource properties.
        resourceOverride(resourceType) {
            return getPropertyEditorComponent(resourceType);
        },

        // Derive display label from meta.label override or the property name.
        propLabel(propName, meta) {
            return meta?.label ?? propName.replace(/_/g, ' ');
        },

        // Row label — the unit suffix used to live inside the number input's
        // own label, which is now owned by PropertyRow.
        rowLabel(propName, meta) {
            const label = this.propLabel(propName, meta);
            return meta?.unit ? `${label} (${meta.unit})` : label;
        },

        isWide(propName, meta) {
            return WIDE_PROPERTIES.includes(propName) || meta?.wide === true;
        },

        // Enum option helpers — support both plain scalars and { value, label } objects.
        optVal(v)   { return typeof v === 'object' && v !== null ? v.value : v; },
        optLabel(v) { return typeof v === 'object' && v !== null ? v.label : String(v); },

        // Toggle keep_circle — node.toggleKeepCircle() writes the new radius
        // (999/5) and flag directly via setStyle; just force the input to
        // re-key so it picks up the new resolved value.
        toggleKeepCircle(propName) {
            this.node.toggleKeepCircle(this.partName);
            this.radiusTick++;
        },
    },
};
</script>
